// src/widgets/ImportPreview.jsx
import { useEffect, useState, useRef } from 'react'
import { FileSpreadsheet, X, Loader2 } from 'lucide-react'
import ConfirmModal from '@/widgets/ConfirmModal'
import ImportResultModal from '@/widgets/ImportResultModal'
import { bulkAddLearnersJson } from '@/api/kuizy'
import * as XLSX from 'xlsx'
import { motion, AnimatePresence } from 'framer-motion'
import Select from 'react-select'

const SAMPLE_LIMIT = 100
const REQUIRED_FIELDS = ['email*', 'first_name*', 'last_name*']
const groupOptions = groups => [
  { value: 'member', label: 'Member' },
  ...groups.map(g => ({ value: g.id, label: g.name })),
]

export default function ImportPreview({ info, groups = [], onConfirm, onCancel }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [headers, setHeaders] = useState([])
  const [errMap, setErrMap] = useState({})
  const [errRows, setErrRows] = useState(new Set())
  const [editing, setEditing] = useState({ row: -1, col: '' })
  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState(null)
  const [result, setResult] = useState(null)
  const [group, setGroup] = useState({ value: 'member', label: 'Member' })
  const inputRef = useRef(null)

  useEffect(() => {
    if (!info) return;
    (async () => {
      try {
        let data = info.fixed || [];

        // ถ้าไม่มี info.fixed ให้ไปอ่านไฟล์โดยตรง
        if (!data.length && info.file) {
          const wb = XLSX.read(await info.file.arrayBuffer(), { type: 'array' });

          // 🔥 1. เลือก Sheet ให้ถูก (DataImport อยู่ index 2 หรือใช้ชื่อหา)
          const targetSheet = wb.SheetNames.find(n => n === 'DataImport') || wb.SheetNames[0];
          const raw = XLSX.utils.sheet_to_json(wb.Sheets[targetSheet], { defval: '' });

          // 🔥 2. FILTER ข้อมูลที่ว่างทิ้งทันที!
          data = raw.filter(row => {
            const fn = `${row["first_name*"] || ""}`.trim();
            const ln = `${row["last_name*"] || ""}`.trim();
            const em = `${row["email*"] || ""}`.trim();
            // เก็บไว้เฉพาะแถวที่มีการกรอกข้อมูลอย่างใดอย่างหนึ่งใน 3 ฟิลด์หลัก
            return fn !== "" || ln !== "" || em !== "";
          });
        }

        setRows(data);
        // ตั้งค่า Header จาก Object keys ของแถวแรกที่เหลืออยู่
        setHeaders(Object.keys(data[0] || {}));

        // --- ส่วนการจัดการ Error Map ---
        const map = {};
        const eSet = new Set();

        (info.errCells || []).forEach(e => {
          map[e.row] = map[e.row] || new Set();

          // รองรับทั้งแบบที่ส่งมาเป็น e.cols (array) หรือ e.col (string)
          if (Array.isArray(e.cols)) {
            e.cols.forEach(c => map[e.row].add(c));
          } else if (e.col) {
            map[e.row].add(e.col);
          }

          eSet.add(e.row);
        });

        setErrMap(map);
        setErrRows(eSet);

      } catch (error) {
        console.error("Preview Error:", error);
        setAlert('ไม่สามารถอ่านข้อมูล');
      } finally {
        setLoading(false);
      }
    })();
  }, [info]);

  const startEdit = (r, c) => {
    setEditing({ row: r, col: c })
    setTimeout(() => inputRef.current?.focus(), 0)
  }
  const commit = (e, r, c) => {
    const val = e.target.value
    setRows(prev => {
      const copy = [...prev]
      copy[r] = { ...copy[r], [c]: val }
      return copy
    })
    setErrMap(prev => {
      if (!prev[r]) return prev
      const next = { ...prev, [r]: new Set(prev[r]) }
      next[r].delete(c)
      if (next[r].size === 0) delete next[r]
      return next
    })
    setErrRows(prev => {
      const next = new Set(prev)
      if (!errMap[r] || errMap[r].size === 1) next.delete(r)
      return next
    })
    setEditing({ row: -1, col: '' })
  }

  const rowsToShow = (() => {
    if (SAMPLE_LIMIT === 0) return rows.map((_, i) => i)
    const base = [...Array(Math.min(SAMPLE_LIMIT, rows.length)).keys()]
    const extra = [...errRows].filter(i => i >= SAMPLE_LIMIT && i < rows.length)
    return [...base, ...extra].sort((a, b) => a - b)
  })()

  const uid = localStorage.getItem('userId') || ''
  const handleImport = async () => {
    if (!group.value) {
      setAlert('กรุณาเลือกกลุ่มผู้เรียนก่อนนำเข้า')
      return
    }
    // 1. กรองแถวที่ไม่มี Error
    const validRows = rows.filter((_r, i) => !errMap[i] || errMap[i].size === 0);

    if (validRows.length === 0) {
      setAlert('ยังมีแถวผิดพลาด — กรุณาแก้ไขก่อนนำเข้า');
      return;
    }

    console.log('validRows : ',validRows);
    
    // 🔥 2. TRANSFORM: เปลี่ยน Key จากที่มี * เป็นชื่อปกติที่ Backend ต้องการ
    const dataToSave = validRows.map(row => ({
      // ใช้ชื่อใหม่ : ค่าจากชื่อเก่า (รองรับทั้งแบบมี space และไม่มี space หน้า *)
      email: row["email*"],
      first_name: row["first_name*"],
      last_name: row["last_name*"],
 
      country: row.country,
      phone: row.phone,
      job_title: row.job_title,
      birth_year: row.birth_year,
      classroom_name: row.classroom_name,
      classroom_short_name: row.classroom_short_name,
      group_name: row.group_name,
      group_short_name: row.group_short_name,
      classroom_id: row.classroom_id,
      group_id: row.group_id,
    }));
    setSaving(true)
    try {
      console.log('dataToSave', dataToSave);

      const { data } = await bulkAddLearnersJson(dataToSave, uid, group.value)
      setResult(data)
      onConfirm && onConfirm()
    } catch {
      setAlert('นำเข้าข้อมูลไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  if (!info) return null

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onCancel}
          className="fixed inset-0 bg-black/45 backdrop-blur-[2px] z-40"
        />
        <motion.div
          key="dialog"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed z-50 inset-x-4 md:inset-x-0 mx-auto max-w-7xl w-full bg-white rounded-xl shadow-2xl flex flex-col max-h-[95vh]"
        >
          <header className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <FileSpreadsheet size={18} /> ตรวจสอบ / แก้ไขก่อนนำเข้า
            </h2>
            <button onClick={onCancel} className="text-gray-500 hover:text-red-500">
              <X size={20} />
            </button>
          </header>

          <div className="flex flex-col gap-6 px-6 py-5 overflow-y-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-1">
                <h3 className="font-semibold">รายละเอียดไฟล์</h3>
                <p className="text-sm">
                  <b>ไฟล์:</b> {info.file?.name || '-'} | <b>แถว:</b> {rows.length}
                </p>
                {errRows.size > 0 && (
                  <p className="text-xs text-red-600">
                    พบแถวผิดพลาด {errRows.size} แถว (ไฮไลท์แดง)
                  </p>
                )}
                <p className="text-xs text-gray-500">ดับเบิลคลิกเซลล์เพื่อแก้ไข</p>
              </div>
              {/* <div className="lg:w-72 space-y-1">
                <h3 className="font-semibold">เลือกกลุ่มผู้เรียนที่จะเพิ่มผู้เรียน</h3>
                <Select
                  classNamePrefix="rs"
                  options={groupOptions(groups)}
                  value={group}
                  onChange={setGroup}
                  isSearchable
                />
              </div> */}
            </div>

            {loading ? (
              <div className="flex justify-center">
                <Loader2 className="animate-spin text-primary" />
              </div>
            ) : (
              <div className="-mx-4 overflow-x-auto">
                <table className="min-w-full w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-12 text-left">#</th>
                      {headers.map(h => (
                        <th
                          key={h}
                          className={`py-2 px-3 text-left ${h === 'email' ? 'w-64' : ''
                            }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rowsToShow.map(ri => {
                      const rowErr = errRows.has(ri)
                      return (
                        <tr
                          key={ri}
                          className={rowErr ? 'bg-rose-50/30' : ''}
                        >
                          <td className="py-2 px-3">{ri + 1}</td>
                          {headers.map(col => {
                            const isErr = errMap[ri]?.has(col)
                            const isEd =
                              editing.row === ri && editing.col === col
                            const isEmptyImportant =
                              !rows[ri][col] && REQUIRED_FIELDS.includes(col)
                            return (
                              <td
                                key={col}
                                className={`py-2 px-3 relative min-w-0 whitespace-pre-wrap break-words ${isErr
                                  ? 'bg-rose-50 text-rose-700 border-rose-400 ring-2 ring-rose-300'
                                  : isEmptyImportant
                                    ? 'bg-yellow-50 border-yellow-300'
                                    : ''
                                  }`}
                                onDoubleClick={() => startEdit(ri, col)}
                              >
                                {isEd ? (
                                  <input
                                    ref={inputRef}
                                    defaultValue={rows[ri][col]}
                                    onBlur={e => commit(e, ri, col)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter')
                                        commit(e, ri, col)
                                      if (e.key === 'Escape')
                                        setEditing({ row: -1, col: '' })
                                    }}
                                    className="absolute inset-0 w-full h-full px-1 outline-2 outline-primary rounded"
                                  />
                                ) : (
                                  rows[ri][col]
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {rows.length > rowsToShow.length && (
                  <p className="text-center text-xs mt-2">
                    … แสดง {rowsToShow.length}/{rows.length} แถว …
                  </p>
                )}
              </div>
            )}
          </div>

          <footer className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t">
            <button
              onClick={onCancel}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg px-4 py-2 text-sm"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleImport}
              disabled={rows.length === 0 || saving}
              className="bg-primary hover:bg-secondary disabled:opacity-40 text-white rounded-lg px-4 py-2 text-sm"
            >
              {saving ? 'กำลังนำเข้า…' : 'นำเข้าข้อมูล'}
            </button>
          </footer>
        </motion.div>
      </AnimatePresence>

      {alert && (
        <ConfirmModal
          type="alert"
          title="แจ้งเตือน"
          message={alert}
          onClose={() => setAlert(null)}
        />
      )}

      {result && (
        <ImportResultModal
          result={result}
          onClose={() => {
            setResult(null)
            onCancel()
          }}
        />
      )}
    </>
  )
}
